import { Col, Row, Image } from 'antd'
import './../Checkpoints.style.less'
import {
  isEmpty,
  isObjectLike,
  map,
  orderBy,
  toString,
  find,
  size,
  includes,
} from 'lodash'
import { REPORT_CONTROL_TYPES } from '../../../InSpectionReport.constant'
import moment from 'moment'
import { DATE_FORMAT } from '@/constant/date'
import { isEmptyValue } from '@/utils/common'
import { InspectionTemplateType } from '@/constant/qsf'
import GroupEntryTableComponent from '@/components/GroupEntryTableComponent'
import { SECTION_NAMES, SUBSECTION_NAMES } from '@/constant/common'

const {
  TEXT,
  NUMBER,
  INTEGER,
  DATE,
  PHOTO,
  SELECT,
  SELECT_DROPDOWN,
  MULTI_SELECT,
  EDITABLE_ENTRY_LIST,
} = REPORT_CONTROL_TYPES

type Props = {
  data: any
  template: any
  reportType?: string
  templateSections?: Array<any>
  subsectionName?: string
  sectionName?: string
  editComponent?: any
  addNewPhotoButton?: any
}

function ControlTypeGroup({
  data,
  template,
  reportType,
  templateSections,
  subsectionName,
  sectionName,
  editComponent = null,
  addNewPhotoButton = null,
}: Props) {
  const { GDS_FABRIC } = InspectionTemplateType
  const errorMessage = 'Error'

  const GRADE_A_THRESHOLD = 20
  const GRADE_B_THRESHOLD = 28

  const WIDE_COLUMNS = {
    Fabrication: { span: 5 },
    Remarks: { span: 5 },
  }

  const formatNumber = (num) => {
    if (isNaN(num)) return errorMessage
    if (Number.isInteger(num)) return num.toString()
    return parseFloat(num.toFixed(2)).toString()
  }
  const findCheckPointTemplateByName = (name) => {
    return template?.checkpoints?.find((checkpoint) => checkpoint.name === name)
  }
  const renderAddEditCheckpoint = ({
    entryIndex = null,
    photoSelected = null,
    addNewPhotoKey = '',
    checkpointKey = '',
  }) => {
    return editComponent ? (
      <Col span={1}>
        {addNewPhotoKey
          ? addNewPhotoButton({
              entryIndex,
              photoSelected,
              addNewPhotoKey,
              checkpointKey,
            })
          : editComponent({
              entryIndex,
              photoSelected,
              addNewPhotoKey,
              checkpointKey,
            })}
      </Col>
    ) : (
      <></>
    )
  }

  const getCellValue = (item: any) => {
    const {
      PHOTO,
      DATE,
      SELECT,
      SELECT_DROPDOWN,
      MULTI_SELECT,
      EDITABLE_ENTRY_LIST,
    } = REPORT_CONTROL_TYPES

    switch (item.control_type) {
      case PHOTO:
        return !isEmptyValue(item.value)
          ? item.value.map((photo) => photo?.comment || '-').join(', ')
          : '-'
      case DATE:
        return displayDateDate(item.value)
      case SELECT:
      case SELECT_DROPDOWN:
        return displayObjectData(item.value)
      case MULTI_SELECT:
        return displayMultiObjectData(item.value)
      case EDITABLE_ENTRY_LIST:
        return item.value?.join(', ') || '-'
      default:
        return displayTextData(item.value)
    }
  }

  const temp = []
  data?.value?.forEach((item) => {
    const tmpArray = []
    for (const [key, value] of Object.entries(item)) {
      const template = findCheckPointTemplateByName(key)

      if (template?.options?.hidden) continue
      const mappedDate = {
        key,
        title: template?.title,
        value,
        index: template?.index,
        control_type: template?.control_type,
      }
      tmpArray.push(mappedDate)
    }
    temp.push(orderBy(tmpArray, 'index'))
  })

  const isGeneralFabricInspectedRolls =
    sectionName === SECTION_NAMES.GENERAL_FABRIC_QUALITY_CHECK &&
    subsectionName === SUBSECTION_NAMES.INSPECTED_ROLLS
  const isWashTestInspectedRolls =
    sectionName === SECTION_NAMES.WASHTESTREPORT &&
    subsectionName === SUBSECTION_NAMES.INSPECTED_ROLLS
  const isSummaryLot =
    sectionName === SECTION_NAMES.SUMMARY_STATUS &&
    subsectionName === SUBSECTION_NAMES.SUMMARY_LOT

  if (isWashTestInspectedRolls && includes([GDS_FABRIC], reportType)) {
    temp.forEach((group) => {
      const beforeWashLengthItem = find(group, { key: 'beforeWashLength' })
      const afterWashLengthItem = find(group, { key: 'afterWashLength' })
      const beforeWashWidthItem = find(group, { key: 'beforeWashWidth' })
      const afterWashWidthItem = find(group, { key: 'afterWashWidth' })

      if (
        beforeWashLengthItem &&
        afterWashLengthItem &&
        beforeWashWidthItem &&
        afterWashWidthItem
      ) {
        const extractNumberAndUnit = (value) => {
          if (isEmptyValue(value)) return { number: null, unit: '' }
          const match = value.match(/^(\d*\.?\d*)\s*(\D+)?$/)
          if (!match) return { number: null, unit: '' }
          const number = parseFloat(match[1])
          const unit = match[2] || ''
          return { number: isNaN(number) ? null : number, unit }
        }
        const beforeWashLength = extractNumberAndUnit(
          beforeWashLengthItem?.value
        )
        const afterWashLength = extractNumberAndUnit(afterWashLengthItem?.value)
        const beforeWashWidth = extractNumberAndUnit(beforeWashWidthItem?.value)
        const afterWashWidth = extractNumberAndUnit(afterWashWidthItem?.value)

        const isValidLength =
          beforeWashLength.number !== null && afterWashLength.number !== null
        const isValidWidth =
          beforeWashWidth.number !== null && afterWashWidth.number !== null

        let lengthDiscrepancy = 0
        let lengthDiscrepancyPercent = 0
        let widthDiscrepancy = 0
        let widthDiscrepancyPercent = 0

        if (isValidLength) {
          const beforeWashLengthValue = beforeWashLength.number
          const afterWashLengthValue = afterWashLength.number
          lengthDiscrepancy = afterWashLengthValue - beforeWashLengthValue
          if (beforeWashLengthValue !== 0) {
            lengthDiscrepancyPercent =
              (lengthDiscrepancy / beforeWashLengthValue) * 100
          }
        }

        if (isValidWidth) {
          const beforeWashWidthValue = beforeWashWidth.number
          const afterWashWidthValue = afterWashWidth.number
          widthDiscrepancy = afterWashWidthValue - beforeWashWidthValue
          if (beforeWashWidthValue !== 0) {
            widthDiscrepancyPercent =
              (widthDiscrepancy / beforeWashWidthValue) * 100
          }
        }
        const lengthDiscrepancyIndex = group.findIndex(
          (item) => item.key === 'afterWashLength'
        )

        const newFields = [
          {
            key: 'lengthDiscrepancy',
            title: 'Length Discrepancy',
            value: isValidLength
              ? formatNumber(lengthDiscrepancy) + beforeWashLength.unit
              : errorMessage,
            control_type: TEXT,
            index: afterWashLengthItem.index + 0.1,
          },
          {
            key: 'lengthDiscrepancyPercent',
            title: 'Length Discrepancy %',
            value:
              isValidLength && beforeWashLength.number !== 0
                ? formatNumber(lengthDiscrepancyPercent) + '%'
                : errorMessage,
            control_type: TEXT,
            index: afterWashLengthItem.index + 0.2,
          },
          {
            key: 'widthDiscrepancy',
            title: 'Width Discrepancy',
            value: isValidWidth
              ? formatNumber(widthDiscrepancy) + beforeWashWidth.unit
              : errorMessage,
            control_type: TEXT,
            index: afterWashWidthItem.index + 0.1,
          },
          {
            key: 'widthDiscrepancyPercent',
            title: 'Width Discrepancy %',
            value:
              isValidWidth && beforeWashWidth.number !== 0
                ? formatNumber(widthDiscrepancyPercent) + '%'
                : errorMessage,
            control_type: TEXT,
            index: afterWashWidthItem.index + 0.2,
          },
        ]

        group.splice(lengthDiscrepancyIndex + 1, 0, newFields[0], newFields[1])

        const updatedWidthDiscrepancyIndex = group.findIndex(
          (item) => item.key === 'afterWashWidth'
        )
        group.splice(
          updatedWidthDiscrepancyIndex + 1,
          0,
          newFields[2],
          newFields[3]
        )
        group.forEach((item, idx) => {
          item.index = idx
        })
      }
    })
  }
  if (isGeneralFabricInspectedRolls && includes([GDS_FABRIC], reportType)) {
    temp.forEach((group) => {
      const widthItem = find(group, { key: 'width' })
      const lengthItem = find(group, { key: 'length' })
      const defectsItem = find(group, { key: 'defectPoint' })

      if (widthItem && lengthItem && defectsItem) {
        const width = parseFloat(widthItem?.value)
        const length = parseFloat(lengthItem?.value)
        const isValidWidth = !isNaN(width) && width > 0
        const isValidLength = !isNaN(length) && length > 0

        let totalDefects = 0
        let isValidDefectPoint = true
        if (
          defectsItem?.control_type === MULTI_SELECT &&
          Array.isArray(defectsItem?.value) &&
          defectsItem?.value.length > 0
        ) {
          const values = defectsItem.value
            .map((item) => item?.label)
            .filter(Boolean)
          totalDefects = values.reduce((sum, val) => {
            if (!val || isEmptyValue(val)) {
              isValidDefectPoint = false
              return sum
            }
            const trimmedVal = val.trim()
            const regex = /(\d+(?:\.\d+)?)\s*$/
            const match = trimmedVal.match(regex)
            if (!match) {
              isValidDefectPoint = false
              return sum
            }
            const number = parseFloat(match[0])
            if (isNaN(number)) {
              isValidDefectPoint = false
              return sum
            }
            return sum + number
          }, 0)
        } else if (
          defectsItem?.control_type === EDITABLE_ENTRY_LIST &&
          Array.isArray(defectsItem?.value) &&
          defectsItem?.value.length > 0
        ) {
          totalDefects = defectsItem.value.reduce((sum, val) => {
            if (!val || isEmptyValue(val)) {
              isValidDefectPoint = false
              return sum
            }
            const trimmedVal = val.trim()
            const regex = /(\d+(?:\.\d+)?)\s*$/
            const match = trimmedVal.match(regex)
            if (!match) {
              isValidDefectPoint = false
              return sum
            }
            const number = parseFloat(match[1])
            if (isNaN(number)) {
              isValidDefectPoint = false
              return sum
            }
            return sum + number
          }, 0)
        } else {
          const value = toString(defectsItem?.value).trim()
          if (!value || isEmptyValue(value)) {
            isValidDefectPoint = false
          } else {
            const defectEntries = value
              .split(/[\n,;\/]+|\s{2,}/)
              .map((entry) => entry.trim())
              .filter(Boolean)
            totalDefects = defectEntries.reduce((sum, entry) => {
              const regex = /(\d+(?:\.\d+)?)\s*$/
              const match = entry.match(regex)
              if (!match) {
                isValidDefectPoint = false
                return sum
              }
              const number = parseFloat(match[1])
              if (isNaN(number)) {
                isValidDefectPoint = false
                return sum
              }
              return sum + number
            }, 0)
          }
        }
        const isValidForPointsAndGrade =
          isValidDefectPoint && isValidWidth && isValidLength
        let points100SqYds = 0
        let grade = 'REJECT'
        if (isValidForPointsAndGrade) {
          points100SqYds = (totalDefects * 36 * 100) / width / length
          if (points100SqYds < GRADE_A_THRESHOLD) {
            grade = 'A'
          } else if (
            points100SqYds >= GRADE_A_THRESHOLD &&
            points100SqYds < GRADE_B_THRESHOLD
          ) {
            grade = 'B'
          }
        }

        const defectPointIndex = group.findIndex(
          (item) => item.key === 'defectPoint'
        )
        const insertIndex = defectPointIndex + 1

        const newFields = [
          {
            key: 'total_defects',
            title: 'Total Defects / Points',
            value: isValidDefectPoint
              ? formatNumber(totalDefects)
              : errorMessage,
            control_type: TEXT,
            index: defectsItem.index + 0.1,
          },
          {
            key: 'points_100_sqr_yds',
            title: 'Points 100 SQR YDS',
            value: isValidForPointsAndGrade
              ? formatNumber(points100SqYds) + '%'
              : errorMessage,
            control_type: TEXT,
            index: defectsItem.index + 0.2,
          },
          {
            key: 'grade',
            title: 'Grade',
            value: isValidForPointsAndGrade ? grade : errorMessage,
            control_type: TEXT,
            index: defectsItem.index + 0.3,
          },
        ]

        group.splice(insertIndex, 0, ...newFields)

        group.forEach((item, idx) => {
          item.index = idx
        })
      }
    })
  }
  const displayObjectData = (displayValue) => {
    return isObjectLike(displayValue) && !isEmpty(displayValue)
      ? displayValue.label
      : '-'
  }

  const displayTextData = (displayValue) => {
    return !isEmptyValue(displayValue) ? toString(displayValue) : '-'
  }

  const displayDateDate = (displayValue) => {
    return !isEmptyValue(displayValue)
      ? moment(displayValue).format(DATE_FORMAT.DDMMYY)
      : '-'
  }

  const displayMultiObjectData = (displayValue) => {
    return isObjectLike(displayValue) && !isEmpty(displayValue)
      ? displayValue.map((item) => item.label).join(', ')
      : '-'
  }

  const enum DisplayType {
    comma = 'comma',
    new_line = 'new_line',
  }

  const displayMultiDataDynamic = (displayValue, type: DisplayType) => {
    const mappedData = map(displayValue, (item) => item)
    if (isEmpty(mappedData)) {
      return '-'
    }

    return type === DisplayType.new_line
      ? mappedData.join('\n')
      : mappedData.join(', ')
  }

  const isContainMultiPhotos = (group) => {
    if (group?.some((groupItem) => groupItem.control_type === PHOTO)) {
      const photosItem = group.filter(
        (groupItem) => groupItem.control_type === PHOTO
      )
      if (photosItem.some((photoControl) => photoControl.value.length > 1)) {
        return true
      }
    }
    return false
  }

  if (isSummaryLot && includes([GDS_FABRIC], reportType)) {
    return (
      <GroupEntryTableComponent
        temp={temp}
        getCellValue={getCellValue}
        WIDE_COLUMNS={WIDE_COLUMNS}
      />
    )
  }
  function RenderCheckPoint({ data, entryIndex }: any) {
    const isGdsFabric = reportType === GDS_FABRIC

    const renderDefaultLayout = (value) => (
      <div>
        <div className="wrap-checkpoint-title">
          <p className="label">{data?.title}</p>
        </div>
        <p className="value">{value}</p>
      </div>
    )

    const renderGdsFabricLayout = (value) => (
      <Row>
        <Col span={12}>
          <p className="gds-label">{data?.title}</p>
        </Col>
        <Col span={12}>
          <p
            className="gds-value"
            style={value === errorMessage ? { color: '#C03261' } : {}}
          >
            {value}
          </p>
        </Col>
      </Row>
    )
    const renderLayout = (value) =>
      isGdsFabric ? renderGdsFabricLayout(value) : renderDefaultLayout(value)

    switch (data.control_type) {
      case TEXT:
      case NUMBER:
      case INTEGER:
        return renderLayout(displayTextData(data?.value))
      case DATE:
        return renderLayout(displayDateDate(data?.value))
      case PHOTO:
        return (
          <div>
            <div className="wrap-checkpoint-title">
              <p className="label">{data?.title}</p>
            </div>
            <div className="group-image-control-container">
              {!isEmptyValue(data?.value)
                ? data?.value?.map((photo, key) => {
                    return (
                      <div className="group-image-wrapper" key={key}>
                        <Image className="group-image" src={photo?.media_url} />
                        <div className="wrap-comment-edit">
                          <p className="comment">{photo?.comment}</p>
                          {renderAddEditCheckpoint({
                            entryIndex: entryIndex,
                            photoSelected: {
                              key,
                              ...photo,
                            },
                            checkpointKey: data.key,
                          })}
                        </div>
                      </div>
                    )
                  })
                : '-'}
            </div>
            {renderAddEditCheckpoint({
              entryIndex: entryIndex,
              photoSelected: null,
              addNewPhotoKey: `${size(data?.value)}`,
              checkpointKey: data.key,
            })}
          </div>
        )
      case SELECT:
      case SELECT_DROPDOWN:
        return renderLayout(displayObjectData(data?.value))
      case MULTI_SELECT:
        return renderLayout(displayMultiObjectData(data?.value))
      case EDITABLE_ENTRY_LIST:
        return renderLayout(
          displayMultiDataDynamic(
            data?.value,
            isGdsFabric ? DisplayType.new_line : DisplayType.comma
          )
        )

      default:
        return <></>
    }
  }

  return (
    <Col span={24}>
      <div className="control-type-group page-break-inside">
        <Row gutter={[16, 16]}>
          {temp?.map((group, key) => {
            const displayEntryLabel = includes(
              [GDS_FABRIC],
              reportType
            ) ? null : (
              <p>Entry #{key + 1}</p>
            )

            return (
              <Col span={isContainMultiPhotos(group) ? 24 : 12} key={key}>
                <div className="group-container">
                  <div className="wrap-entry-title">
                    {displayEntryLabel}
                    <div className="group-checkpoint-edit">
                      {renderAddEditCheckpoint({
                        entryIndex: key,
                        photoSelected: null,
                      })}
                    </div>
                  </div>
                  {map(group, (groupItem, indexKey) => {
                    return (
                      <div className="checkpoint" key={indexKey}>
                        <RenderCheckPoint data={groupItem} entryIndex={key} />
                      </div>
                    )
                  })}
                </div>
              </Col>
            )
          })}
        </Row>
      </div>
    </Col>
  )
}

export default ControlTypeGroup
